import React, { useState } from "react";
import { cn } from "../../lib/cn";

interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "prefix"> {
  prefixElement?: React.ReactNode;
  suffixElement?: React.ReactNode;
  error?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ prefixElement, suffixElement, error = false, className, onFocus, onBlur, ...props }, ref) => {
    const [focused, setFocused] = useState(false);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(false);
      onBlur?.(e);
    };

    return (
      <div
        className={cn(
          "group inline-flex items-center gap-2",
          "h-9 w-full rounded-[var(--radius-md)]",
          "bg-[var(--bg-surface)] backdrop-blur-[var(--blur-sm)]",
          "border border-[var(--border-hairline)]",
          "transition-all duration-[var(--duration-standard)] ease-[var(--easing-default)]",
          focused && [
            "border-[var(--color-cool-azure)]/40",
            "shadow-[var(--glow-focus)]",
          ],
          error && [
            "border-[var(--color-soft-red)]/40",
            "shadow-[var(--glow-error)]",
          ],
          props.disabled && "opacity-50 pointer-events-none",
          className,
        )}
      >
        {prefixElement && (
          <span className="flex shrink-0 items-center pl-3 text-[var(--text-muted)]">
            {prefixElement}
          </span>
        )}
        <input
          ref={ref}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={cn(
            "h-full w-full bg-transparent text-sm text-[var(--text-primary)]",
            "placeholder:text-[var(--text-muted)]",
            "outline-none border-none",
            "caret-[var(--color-lever-orange)]",
            !prefixElement && "pl-3",
            !suffixElement && "pr-3",
          )}
          {...props}
        />
        {suffixElement && (
          <span className="flex shrink-0 items-center pr-3 text-[var(--text-muted)]">
            {suffixElement}
          </span>
        )}
      </div>
    );
  },
);

Input.displayName = "Input";

export { Input };
export type { InputProps };
